/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useMemo, useState, useCallback, memo, useEffect } from 'react'
import colors from '@sportninja/common/constants/appColors'
import Text from '../ts-components/Text'
import { DeleteOutlined, EditOutlined, FlagOutlined } from '@ant-design/icons'
import { Dropdown, Image, App } from 'antd'
import sportninjaLogo from './sportninja-post.png'
import canlanLogo from './canlan-post.png'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import {
  getFeedPostDate,
  getImageThumbnailId,
} from '@sportninja/common/utils/utils'
import TopComment from './TopComment'
import { Reply } from '@sportninja/common/hooks/useSocialFeed'
import { MoreIcon } from './MoreIcon'
import AddComment from './AddComment'
import FeedItemDetailsModal from './FeedItemDetailsModal'
import PostInsightsModal from './PostInsightsModal'
import DeleteModal from '../Modal/DeleteModal'
import ShareModal from '../ShareModal'
import RichTextRenderer from '../RichTextRenderer'
import ProfileLink from './ProfileLink'
import DeleteCommentButton from './DeleteCommentButton'
import InsightsIcon from '../icons/InsightsIcon'
import {
  LikeButton,
  CommentButton,
  ShareButton,
  ExternalLinkButton,
} from './ActionButtons'
import {
  feedItemContainerStyles,
  headerStyles,
  moreIconStyles,
  contentStyles,
  imageContainerStyles,
  actionsStyles,
  actionsGroupStyles,
  separatorStyles,
  insightsButtonStyles,
  headerActionsStyles,
} from './FeedItem.styles'
import {
  likeComment,
  dislikeComment,
  deleteComment,
  parseUrlHost,
  getPlaceholderImage,
  trackPostView,
  PLACEHOLDER_IMAGES,
} from './utils'

// Improved TypeScript interfaces
interface FeedItemProps {
  sendSocialFeed?: boolean
  sendEmail?: boolean
  sendPushNotification?: boolean
  setIsModalOpen: (open: boolean) => void
  entityType: 'team' | 'schedule' | 'organization'
  title: string
  body: string
  imageUrl?: string
  linkUrl?: string
  likesCount: number
  commentsCount?: number
  createdAt: string
  avatarUrl?: string
  onCommentClick?: () => void
  onDeleteClick: () => void
  onReplySubmit?: (replyContent: string) => void
  onLikeClick: () => void
  onReportClick?: () => void
  entityName: string
  canEdit?: boolean
  canDelete?: boolean
  isAppPost?: boolean
  userAvatarUrl?: string
  entityId: string
  isLiked?: boolean
  userId?: string
  userName?: string
  userProfilePicture?: string
  onEditClick?: () => void
  comments?: Reply[]
  postId: string
  selfUserId?: string
  selfUserName?: string
  fetchPosts: (pageNumber: number, shouldDisplayLoading?: boolean) => void
  canReply?: boolean
  shouldAutoOpenModal?: boolean
  onModalAutoOpened?: () => void
  role_types?: {
    id: number
    name: string
  }[]
  analyticsType?: 'social_feed' | 'email' | 'push'
}

interface DropdownMenuItem {
  label: string
  key: string
  icon: React.ReactNode
  onClick?: () => void
  danger?: boolean
}

const FeedItem: React.FC<FeedItemProps> = ({
  userProfilePicture,
  entityId,
  entityType,
  title,
  body,
  imageUrl,
  linkUrl,
  likesCount,
  createdAt,
  avatarUrl,
  onDeleteClick,
  onLikeClick,
  onReportClick,
  entityName,
  canEdit = false,
  canDelete = false,
  isAppPost = false,
  isLiked: initialIsLiked = false,
  userName,
  userId,
  onEditClick,

  commentsCount,
  comments = [],
  postId,
  selfUserId,
  selfUserName,
  fetchPosts,
  canReply = true,
  shouldAutoOpenModal = false,
  onModalAutoOpened,
  sendSocialFeed = false,
  sendEmail = false,
  sendPushNotification = false,
  role_types = [],
  analyticsType = 'social_feed',
}) => {
  const topComment = useMemo(() => {
    return comments.length > 0 ? comments[comments.length - 1] : null
  }, [comments])

  const isTopCommentFromSelfUser = useMemo(() => {
    return topComment?.user?.id === selfUserId
  }, [topComment, selfUserId])

  // Local state for optimistic UI updates
  const [isLiked, setIsLiked] = useState(initialIsLiked)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isInsightsModalOpen, setIsInsightsModalOpen] = useState(false)
  const [isShareModalOpen, setIsShareModalOpen] = useState(false)
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null)

  const { message } = App.useApp()

  // Sync local isLiked state with prop when it changes (e.g., after feed refresh)
  useEffect(() => {
    setIsLiked(initialIsLiked)
  }, [initialIsLiked])

  // Memoized computed values
  const isOrgOrSchedulePost = useMemo(
    () => entityType === 'organization' || entityType === 'schedule',
    [entityType]
  )

  const navigationUrl = useMemo(() => {
    if (isAppPost) {
      return undefined
    }
    if (isOrgOrSchedulePost) {
      return `/${
        entityType === 'organization' ? 'org' : 'schedule'
      }/${entityId}`
    }
    return `/team/${entityId}`
  }, [entityId, entityType, isAppPost, isOrgOrSchedulePost])

  const formattedHost = useMemo(() => {
    return linkUrl ? parseUrlHost(linkUrl) : null
  }, [linkUrl])

  const entityTypeMap = {
    organization: 'org',
    team: 'team',
    schedule: 'schedule',
  } as const

  const urlPrefix = entityTypeMap[entityType]
  const shareUrl =
    urlPrefix && entityId && postId
      ? `${window.location.origin}/${urlPrefix}/${entityId}/feed/${postId}`
      : null

  const avatarSrc = useMemo(() => {
    if (isAppPost) {
      return isCanlan ? canlanLogo : sportninjaLogo
    }
    return avatarUrl || getPlaceholderImage(entityType)
  }, [isAppPost, avatarUrl, entityType])

  const userProfileSrc = useMemo(() => {
    return userProfilePicture || getPlaceholderImage(entityType, true)
  }, [userProfilePicture, entityType])

  const formattedDate = useMemo(() => {
    return getFeedPostDate(createdAt)
  }, [createdAt])

  const displayName = useMemo(() => {
    return entityName || 'SportNinja'
  }, [entityName])

  const dateText = useMemo(() => {
    if (userName) {
      return `${userName} - ${formattedDate}`
    }
    return formattedDate
  }, [userName, formattedDate])

  // Memoized dropdown menu items
  const dropdownItems = useMemo((): DropdownMenuItem[] => {
    const items: DropdownMenuItem[] = []

    if (canEdit) {
      items.push({
        label: 'Edit',
        key: 'edit',
        icon: <EditOutlined />,
        onClick: onEditClick,
      })
    }

    items.push({
      label: 'Report',
      key: 'report',
      icon: <FlagOutlined style={{ color: colors.WHITE }} />,
      onClick: onReportClick,
      danger: false,
    })

    if (canDelete) {
      items.push({
        label: 'Delete',
        key: 'delete',
        icon: <DeleteOutlined style={{ color: colors.ERROR_200 }} />,
        onClick: onDeleteClick,
        danger: true,
      })
    }

    return items
  }, [canEdit, canDelete, onEditClick, onDeleteClick, onReportClick])

  // Memoized event handlers
  const handleLikeClick = useCallback(() => {
    setIsLiked((prev) => !prev)
    onLikeClick()
  }, [onLikeClick])

  const handleShareClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (shareUrl) {
        setIsShareModalOpen(true)
      }
    },
    [shareUrl]
  )

  const handleOpenModal = useCallback(() => {
    console.log(
      '🔄 FeedItem: Opening post modal for postId:',
      postId,
      'with analytics type:',
      analyticsType
    )
    // setIsModalOpenProp(true)
    setIsModalOpen(true)

    // Fire-and-forget analytics tracking - won't block modal opening
    trackPostView(postId, analyticsType as 'social_feed' | 'email' | 'push')
  }, [postId, analyticsType])

  const handleCloseModal = useCallback(() => {
    // setIsModalOpenProp(false)
    setIsModalOpen(false)
  }, [])

  const handleOpenInsightsModal = useCallback(() => {
    setIsInsightsModalOpen(true)
  }, [])

  const handleCloseInsightsModal = useCallback(() => {
    setIsInsightsModalOpen(false)
  }, [])

  const handleNavigationClick = useCallback(
    (e: React.MouseEvent) => {
      if (isAppPost) {
        e.preventDefault()
      }
    },
    [isAppPost]
  )

  const handleDeleteComment = useCallback(
    async (_shouldForce = false) => {
      if (!commentToDelete) {
        throw new Error('No comment ID provided')
      }

      try {
        await deleteComment(commentToDelete)
        message.success('Comment deleted successfully.')
        fetchPosts(1, false)
        setCommentToDelete(null)
      } catch (err: any) {
        console.error('Failed to delete comment:', err)
        message.error(err?.message || 'Failed to delete comment')
        throw err
      }
    },
    [commentToDelete, message, fetchPosts]
  )

  const handleDeleteCommentClick = useCallback((commentId: string) => {
    setCommentToDelete(commentId)
    // Use a more reliable method to trigger the delete modal
    setTimeout(() => {
      document.getElementById('delete-comment-button')?.click()
    }, 0)
  }, [])

  // Handle auto-opening modal when shouldAutoOpenModal is true
  useEffect(() => {
    if (shouldAutoOpenModal) {
      console.log(
        '🔄 FeedItem: Auto-opening modal for postId:',
        postId,
        'with analytics type:',
        analyticsType
      )
      setIsModalOpen(true)
      onModalAutoOpened?.()

      // Fire-and-forget analytics tracking for auto-opened modals
      trackPostView(postId, analyticsType as 'social_feed' | 'email' | 'push')
    }
  }, [shouldAutoOpenModal, onModalAutoOpened, postId, analyticsType])

  const navigationCursor = useMemo(
    () => (isAppPost ? 'default' : 'pointer'),
    [isAppPost]
  )

  const shouldShowInsightsButton = useMemo(
    () => canEdit && !isAppPost,
    [canEdit, isAppPost]
  )

  return (
    <>
      <DeleteModal
        title='Delete Comment'
        message='Are you sure you want to delete this comment? This cannot be undone.'
        onDelete={handleDeleteComment}
        Button={(props) => (
          <DeleteCommentButton id='delete-comment-button' {...props} />
        )}
        forceDeleteText={undefined}
        shouldCloseModalAfterDelete={true}
        useForceDelete={false}
        displayBanner={false}
      />

      <div css={feedItemContainerStyles}>
        {/* Header */}
        <div css={headerStyles}>
          <ProfileLink
            entityType={entityType}
            isAppPost={isAppPost}
            avatarSrc={avatarSrc}
            displayName={displayName}
            formattedDate={formattedDate}
            isOrgOrSchedulePost={isOrgOrSchedulePost}
            userProfileSrc={userProfileSrc}
            entityName={entityName}
            dateText={dateText}
            navigationUrl={navigationUrl}
            navigationCursor={navigationCursor}
            handleNavigationClick={handleNavigationClick}
            role_types={role_types}
          />
          <div css={headerActionsStyles}>
            {shouldShowInsightsButton && (
              <span
                css={insightsButtonStyles}
                onClick={handleOpenInsightsModal}
              >
                <InsightsIcon size={20} color={colors.PRIMARY} />
              </span>
            )}
            <Dropdown
              menu={{ items: dropdownItems }}
              trigger={['click']}
              getPopupContainer={(triggerNode) =>
                triggerNode.parentElement || document.body
              }
              overlayStyle={{ zIndex: 1001 }}
            >
              <span css={moreIconStyles}>
                <MoreIcon color={colors.PRIMARY} />
              </span>
            </Dropdown>
          </div>
        </div>

        {/* Content */}
        <div
          css={contentStyles}
          onClick={handleOpenModal}
          style={{ cursor: 'pointer' }}
        >
          <Text
            variant='paragraphMedium'
            weight='bold'
            color={colors.WHITE}
            style='margin-bottom: 8px; font-size: 18px;'
          >
            {title}
          </Text>
          <RichTextRenderer
            content={body}
            truncateLength={1500}
            showReadMore={true}
            readMoreAlign='left'
          />
        </div>

        {/* Image */}
        {imageUrl && (
          <div
            css={imageContainerStyles}
            onClick={handleOpenModal}
            style={{ cursor: 'pointer' }}
          >
            <Image src={imageUrl} preview={false} draggable={false} />
          </div>
        )}

        {/* Actions */}
        <div css={[actionsStyles, !canReply ? { marginBottom: 0 } : {}]}>
          <div css={actionsGroupStyles}>
            <LikeButton
              isLiked={isLiked}
              likesCount={likesCount}
              onClick={handleLikeClick}
            />
            {canReply ? (
              <CommentButton
                commentsCount={commentsCount}
                onClick={handleOpenModal}
              />
            ) : null}
            {!isAppPost ? <ShareButton onClick={handleShareClick} /> : null}
          </div>
          {formattedHost && (
            <ExternalLinkButton
              linkUrl={linkUrl!}
              formattedHost={formattedHost}
            />
          )}
        </div>

        {canReply && topComment && (
          <TopComment
            avatarUrl={
              getImageThumbnailId(topComment.user) || PLACEHOLDER_IMAGES.user
            }
            userName={topComment?.user?.name_full || ''}
            timeAgo={getFeedPostDate(topComment.created_at)}
            comment={topComment.body}
            likesCount={topComment?.like_count || 0}
            isLiked={topComment?.is_liked}
            onLikeClick={() => likeComment(topComment.id)}
            onDislikeClick={() => dislikeComment(topComment.id)}
            canDelete={isTopCommentFromSelfUser}
            onDeleteClick={() => handleDeleteCommentClick(topComment.id)}
          />
        )}

        {/* See All Comments */}
        {canReply && comments.length > 1 && (
          <span
            onClick={handleOpenModal}
            css={css`
              cursor: pointer;
              margin-top: 8px;
              margin-bottom: 8px;
              display: block;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.PRIMARY}
            >
              See All Comments
            </Text>
          </span>
        )}

        {/* Separator */}
        {canReply && <div css={separatorStyles} />}

        {/* Add Comment */}
        {canReply && (
          <AddComment
            userAvatarUrl={userProfilePicture}
            onCommentClick={handleOpenModal}
          />
        )}

        {/* Feed Item Details Modal */}
        <FeedItemDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          userProfilePicture={userProfilePicture}
          entityId={entityId}
          entityType={entityType}
          entityName={entityName}
          title={title}
          body={body}
          imageUrl={imageUrl}
          linkUrl={linkUrl}
          likesCount={likesCount}
          commentsCount={commentsCount}
          createdAt={createdAt}
          avatarUrl={avatarUrl}
          onLikeClick={onLikeClick}
          isAppPost={isAppPost}
          isLiked={isLiked}
          userName={userName}
          onEditClick={onEditClick}
          comments={comments}
          userId={userId}
          userAvatarUrl={userProfilePicture}
          postId={postId}
          selfUserId={selfUserId}
          selfUserName={selfUserName}
          canReply={canReply}
          fetchPosts={fetchPosts}
          role_types={role_types}
        />

        {/* Post Insights Modal */}
        <PostInsightsModal
          isOpen={isInsightsModalOpen}
          onClose={handleCloseInsightsModal}
          postId={postId}
          sendEmail={sendEmail}
          sendPushNotification={sendPushNotification}
          sendSocialFeed={sendSocialFeed}
          title={title}
          likesCount={likesCount}
          commentsCount={commentsCount}
        />
      </div>

      {postId && shareUrl && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          entityType='post'
          entityName={title || entityName}
          shareUrl={shareUrl}
        />
      )}
    </>
  )
}

export default memo(FeedItem)
